let isUpdating = false;

// Détection de Safari/iOS/macOS
const isApplePlatform = () => {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod|Macintosh|Safari/.test(ua) && !/Chrome/.test(ua);
};

// Détection de l'environnement de développement
const isDevelopment = () => {
  return window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.port === '3000' ||
    window.location.port === '3001';
};

// Version actuelle de l'application, à mettre à jour lors des déploiements
const APP_VERSION = '1.0.53'; // Incrémenter cette valeur à chaque mise à jour

// Clé pour le contrôle du cache iOS
const IOS_LAST_CLEARED_VERSION = 'ios_last_cleared_version';
// Clé pour éviter les boucles de rechargement
const IOS_RELOAD_TIMESTAMP = 'ios_reload_timestamp';
// Délai minimum entre deux rechargements (en ms)
const MIN_RELOAD_INTERVAL = 100000; // 10 secondes

// Fonction pour gérer spécifiquement le problème de cache sur iOS
const handleIOSCacheIssues = async () => {
  if (!isApplePlatform()) return;

  console.log('PWA: Vérification du cache iOS...');

  // Vérifier si nous avons déjà nettoyé le cache pour cette version
  const lastClearedVersion = localStorage.getItem(IOS_LAST_CLEARED_VERSION);

  if (lastClearedVersion === APP_VERSION) {
    console.log('PWA: Cache iOS déjà nettoyé pour cette version');
    return;
  }

  // Vérifier si nous sommes dans une boucle de rechargement
  const lastReloadTimestamp = localStorage.getItem(IOS_RELOAD_TIMESTAMP);
  const currentTime = Date.now();

  if (lastReloadTimestamp) {
    const timeSinceLastReload = currentTime - parseInt(lastReloadTimestamp, 10);

    if (timeSinceLastReload < MIN_RELOAD_INTERVAL) {
      console.log(`PWA: Détection d'une possible boucle de rechargement (${timeSinceLastReload}ms depuis le dernier rechargement)`);
      console.log('PWA: Interruption du cycle de rechargement pour éviter une boucle infinie');

      // Marquer cette version comme nettoyée pour éviter de futurs rechargements
      localStorage.setItem(IOS_LAST_CLEARED_VERSION, APP_VERSION);
      return;
    }
  }

  console.log('PWA: Nouvelle version détectée sur iOS, nettoyage du cache...');

  try {
    // Nettoyer le cache pour les requêtes de navigation
    if ('caches' in window) {
      const cacheKeys = await window.caches.keys();

      for (const cacheKey of cacheKeys) {
        // Ne pas supprimer le cache des images pour préserver la performance
        if (cacheKey.includes('image') || cacheKey.includes('img')) {
          continue;
        }

        console.log(`PWA: Suppression du cache: ${cacheKey}`);
        await window.caches.delete(cacheKey);
      }
    }

    // Marquer cette version comme nettoyée
    localStorage.setItem(IOS_LAST_CLEARED_VERSION, APP_VERSION);
    console.log('PWA: Cache iOS nettoyé avec succès pour la version', APP_VERSION);

    // Si nous sommes sur iOS, recharger la page après un court délai
    if (isApplePlatform()) {
      // Enregistrer le timestamp du rechargement
      localStorage.setItem(IOS_RELOAD_TIMESTAMP, currentTime.toString());

      setTimeout(() => {
        console.log('PWA: Rechargement de la page sur iOS après nettoyage du cache');
        // Forcer le rechargement depuis le serveur en ajoutant un paramètre à l'URL
        const url = new URL(window.location.href);
        url.searchParams.set('cache_buster', Date.now().toString());
        window.location.href = url.toString();
      }, 500);
    }
  } catch (error) {
    console.error('PWA: Erreur lors du nettoyage du cache iOS:', error);
  }
};

export const registerSW = async () => {
  // Appliquer la solution de cache pour iOS immédiatement
  await handleIOSCacheIssues();

  // En développement, simuler l'enregistrement sans réellement le faire
  if (isDevelopment()) {
    console.log('PWA: Environnement de développement détecté, simulation de l\'enregistrement du service worker');

    // Simuler l'événement beforeinstallprompt pour tester l'UI
    setTimeout(() => {
      const mockInstallEvent = new Event('beforeinstallprompt');
      // Empêcher la propagation par défaut
      mockInstallEvent.preventDefault = () => { };
      // Ajouter une méthode prompt simulée
      // @ts-expect-error - Ajout de la méthode prompt au mock event
      mockInstallEvent.prompt = async () => {
        console.log('PWA DEV: Simulation du prompt d\'installation');
        return { outcome: 'accepted' };
      };
      window.dispatchEvent(mockInstallEvent);
    }, 3000);

    return null; // Ne pas réellement enregistrer de service worker en développement
  }

  if ('serviceWorker' in navigator) {
    try {
      // Déterminer quel fichier de service worker utiliser
      // En production, nous utilisons /sw.js, qui est généré par Vite/Workbox
      // En développement, on pourrait utiliser /service-worker.js si nécessaire
      const swPath = '/sw.js';

      console.log(`PWA: Tentative d'enregistrement du service worker: ${swPath}`);
      const registration = await navigator.serviceWorker.register(swPath);
      console.log(`PWA: Service worker enregistré avec succès pour le scope: ${registration.scope}`);

      // Vérifier les mises à jour plus fréquemment sur les appareils Apple
      const updateInterval = isApplePlatform() ? 15 * 60 * 1000 : 60 * 60 * 1000; // 15 min pour Apple, 1h pour les autres
      console.log(`PWA: Configuration de la vérification des mises à jour toutes les ${updateInterval / 60000} minutes`);

      setInterval(() => {
        if (!isUpdating) {
          console.log('PWA: Vérification des mises à jour du service worker...');
          registration.update().catch(err => {
            console.error('PWA: Erreur lors de la mise à jour du service worker:', err);
          });
        }
      }, updateInterval);

      // Écouter les messages du service worker
      navigator.serviceWorker.addEventListener('message', (event) => {
        // Gestion du message de mise à jour de version
        if (event.data && event.data.type === 'VERSION_UPDATE' && !isUpdating) {
          console.log('PWA: Message de mise à jour reçu, version:', event.data.version);
          console.log('PWA: Version précédente:', event.data.previousVersion);

          const lastNotifiedVersion = localStorage.getItem('lastNotifiedVersion');
          console.log('PWA: Dernière version notifiée:', lastNotifiedVersion);

          // Ne pas montrer la notification si c'est la première installation ou si la version est la même
          if (!event.data.previousVersion || event.data.version === event.data.previousVersion) {
            console.log('PWA: Première installation ou même version, pas de notification');
            return;
          }

          // Sur les appareils Apple, forcer le rechargement sans notification
          if (isApplePlatform()) {
            console.log('PWA: Plateforme Apple détectée, rechargement forcé');
            isUpdating = true;
            localStorage.setItem('lastNotifiedVersion', event.data.version);
            localStorage.setItem('pwaUpdatePending', 'true');

            // Vérifier si nous sommes dans une boucle de rechargement
            const lastReloadTimestamp = localStorage.getItem(IOS_RELOAD_TIMESTAMP);
            const currentTime = Date.now();

            if (lastReloadTimestamp) {
              const timeSinceLastReload = currentTime - parseInt(lastReloadTimestamp, 10);

              if (timeSinceLastReload < MIN_RELOAD_INTERVAL) {
                console.log(`PWA: Détection d'une possible boucle de rechargement (${timeSinceLastReload}ms depuis le dernier rechargement)`);
                console.log('PWA: Interruption du cycle de rechargement pour éviter une boucle infinie');
                isUpdating = false;
                return;
              }
            }

            // Enregistrer le timestamp du rechargement
            localStorage.setItem(IOS_RELOAD_TIMESTAMP, currentTime.toString());

            // Forcer le rechargement après un court délai
            setTimeout(() => {
              window.location.reload();
            }, 500);
            return;
          }

          if (!lastNotifiedVersion || lastNotifiedVersion !== event.data.version) {
            console.log('PWA: Affichage de la notification de mise à jour');
            isUpdating = true;
            showUpdateNotification(event.data.version).finally(() => {
              isUpdating = false;
            });
          } else {
            console.log('PWA: Version déjà notifiée, pas de notification');
          }
        }

        // Gestion spécifique des problèmes de cache sur iOS
        if (event.data && event.data.type === 'IOS_CACHE_CLEANUP') {
          console.log('PWA: Message de nettoyage de cache iOS reçu, version:', event.data.version);

          // Vérifier si nous sommes dans une boucle de rechargement
          const lastReloadTimestamp = localStorage.getItem(IOS_RELOAD_TIMESTAMP);
          const currentTime = Date.now();

          if (lastReloadTimestamp) {
            const timeSinceLastReload = currentTime - parseInt(lastReloadTimestamp, 10);

            if (timeSinceLastReload < MIN_RELOAD_INTERVAL) {
              console.log(`PWA: Détection d'une possible boucle de rechargement (${timeSinceLastReload}ms depuis le dernier rechargement)`);
              console.log('PWA: Interruption du cycle de nettoyage de cache pour éviter une boucle infinie');
              return;
            }
          }

          // Nettoyer le cache et recharger la page avec le paramètre cache_buster
          handleIOSCacheIssues().then(() => {
            console.log('PWA: Nettoyage de cache iOS terminé suite au message du service worker');
          });
        }
      });

      // Vérifier si une mise à jour est disponible immédiatement
      if (registration.waiting && !isUpdating) {
        console.log('PWA: Service worker en attente détecté');
        isUpdating = true;

        // Sur les appareils Apple, forcer le rechargement
        if (isApplePlatform()) {
          console.log('PWA: Plateforme Apple avec service worker en attente, rechargement forcé');
          localStorage.setItem('pwaUpdatePending', 'true');

          // Vérifier si nous sommes dans une boucle de rechargement
          const lastReloadTimestamp = localStorage.getItem(IOS_RELOAD_TIMESTAMP);
          const currentTime = Date.now();

          if (lastReloadTimestamp) {
            const timeSinceLastReload = currentTime - parseInt(lastReloadTimestamp, 10);

            if (timeSinceLastReload < MIN_RELOAD_INTERVAL) {
              console.log(`PWA: Détection d'une possible boucle de rechargement (${timeSinceLastReload}ms depuis le dernier rechargement)`);
              console.log('PWA: Interruption du cycle de rechargement pour éviter une boucle infinie');
              isUpdating = false;
              return;
            }
          }

          // Enregistrer le timestamp du rechargement
          localStorage.setItem(IOS_RELOAD_TIMESTAMP, currentTime.toString());

          window.location.reload();
          return;
        }

        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      }

      registration.addEventListener('updatefound', () => {
        if (isUpdating) {
          console.log('PWA: Mise à jour déjà en cours, ignoré');
          return;
        }

        console.log('PWA: Nouvelle mise à jour trouvée');
        const newWorker = registration.installing;

        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            console.log('PWA: État du nouveau service worker:', newWorker.state);
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              console.log('PWA: Nouveau service worker installé');

              // Sur les appareils Apple, forcer le rechargement
              if (isApplePlatform()) {
                console.log('PWA: Plateforme Apple avec nouveau service worker installé, rechargement forcé');
                isUpdating = true;
                localStorage.setItem('pwaUpdatePending', 'true');

                // Vérifier si nous sommes dans une boucle de rechargement
                const lastReloadTimestamp = localStorage.getItem(IOS_RELOAD_TIMESTAMP);
                const currentTime = Date.now();

                if (lastReloadTimestamp) {
                  const timeSinceLastReload = currentTime - parseInt(lastReloadTimestamp, 10);

                  if (timeSinceLastReload < MIN_RELOAD_INTERVAL) {
                    console.log(`PWA: Détection d'une possible boucle de rechargement (${timeSinceLastReload}ms depuis le dernier rechargement)`);
                    console.log('PWA: Interruption du cycle de rechargement pour éviter une boucle infinie');
                    isUpdating = false;
                    return;
                  }
                }

                // Enregistrer le timestamp du rechargement
                localStorage.setItem(IOS_RELOAD_TIMESTAMP, currentTime.toString());

                window.location.reload();
                return;
              }

              console.log('PWA: Demande de skip waiting');
              isUpdating = true;
              newWorker.postMessage({ type: 'SKIP_WAITING' });
            }
          });
        }
      });

      // Écouter les changements de contrôleur
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        console.log('PWA: Nouveau contrôleur service worker actif');
        setTimeout(() => {
          isUpdating = false;
        }, 1000);
      });

      // Vérifier si un rechargement est en attente (pour les appareils Apple)
      if (localStorage.getItem('pwaUpdatePending') === 'true') {
        console.log('PWA: Mise à jour en attente détectée, nettoyage');
        localStorage.removeItem('pwaUpdatePending');

        // Vérifier si nous avons rechargé récemment
        const lastReloadTimestamp = localStorage.getItem(IOS_RELOAD_TIMESTAMP);
        if (lastReloadTimestamp) {
          const currentTime = Date.now();
          const timeSinceLastReload = currentTime - parseInt(lastReloadTimestamp, 10);

          console.log(`PWA: Dernier rechargement il y a ${timeSinceLastReload}ms`);

          // Si le dernier rechargement est récent, considérer que la mise à jour est terminée
          if (timeSinceLastReload < MIN_RELOAD_INTERVAL * 2) {
            console.log('PWA: Rechargement récent détecté, mise à jour considérée comme terminée');
            // Nettoyer le marqueur de timestamp pour éviter de futures confusions
            localStorage.removeItem(IOS_RELOAD_TIMESTAMP);
          }
        }
      }

      return registration;

    } catch (error) {
      console.error('PWA: Erreur lors de l\'enregistrement du service worker:', error);
      isUpdating = false;
      return null;
    }
  } else {
    console.log('PWA: Les Service Workers ne sont pas supportés par ce navigateur');
    return null;
  }
};

const showUpdateNotification = async (version: string) => {
  console.log('PWA: Tentative d\'affichage de la notification pour la version:', version);

  // Supprimer toute notification existante
  const existingNotification = document.getElementById('pwa-update-notification');
  if (existingNotification) {
    console.log('PWA: Notification déjà présente, annulation');
    return Promise.resolve();
  }

  return new Promise<void>((resolve) => {
    const notification = document.createElement('div');
    notification.id = 'pwa-update-notification';
    notification.style.position = 'fixed';
    notification.style.bottom = '20px';
    notification.style.left = '50%';
    notification.style.transform = 'translateX(-50%)';
    notification.style.backgroundColor = '#0A285F';
    notification.style.color = 'white';
    notification.style.padding = '1rem';
    notification.style.borderRadius = '8px';
    notification.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
    notification.style.zIndex = '9999';
    notification.style.display = 'flex';
    notification.style.gap = '1rem';
    notification.style.alignItems = 'center';

    const updateButton = document.createElement('button');
    updateButton.textContent = 'Mettre à jour';
    updateButton.style.background = 'white';
    updateButton.style.color = '#0A285F';
    updateButton.style.border = 'none';
    updateButton.style.padding = '0.5rem 1rem';
    updateButton.style.borderRadius = '4px';
    updateButton.style.cursor = 'pointer';

    const message = document.createElement('div');
    message.textContent = 'Une nouvelle version est disponible !';

    notification.appendChild(message);
    notification.appendChild(updateButton);

    let isReloading = false;

    updateButton.addEventListener('click', async () => {
      if (isReloading) {
        console.log('PWA: Rechargement déjà en cours, ignoré');
        return;
      }

      console.log('PWA: Clic sur le bouton de mise à jour');
      isReloading = true;

      // Enregistrer la version actuelle comme notifiée
      localStorage.setItem('lastNotifiedVersion', version);
      notification.remove();

      // Forcer la mise à jour du service worker
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        console.log('PWA: Nombre de service workers trouvés:', registrations.length);

        for (const registration of registrations) {
          if (registration.waiting) {
            console.log('PWA: Envoi du message SKIP_WAITING au service worker en attente');
            registration.waiting.postMessage({ type: 'SKIP_WAITING' });
          }
        }

        // Nettoyer le localStorage des anciennes versions
        console.log('PWA: Nettoyage du localStorage');
        localStorage.removeItem('lastNotifiedVersion');
      }

      // Recharger la page après un court délai pour laisser le temps au service worker de s'activer
      console.log('PWA: Programmation du rechargement de la page');
      setTimeout(() => {
        console.log('PWA: Rechargement de la page');
        window.location.reload();
        resolve();
      }, 1000);
    });

    document.body.appendChild(notification);
    console.log('PWA: Notification affichée');
  });
}; 